import { useCallback, useEffect, useMemo } from 'react';

import { FilterChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { MultiSelect } from 'primereact/multiselect';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useShippingSites } from '@/services/shipping/hooks';
import type { SiteRef } from '@/services/shipping/types';

const TARGET_COLUMN = 'shipFromSiteId';
const SEARCH_PARAM = 'sIds';

export function ShipFromSiteDropdown({
  grid,
}: {
  grid: React.RefObject<AgGridReact>;
}) {
  const { t } = useTranslation();

  const { data: sites } = useShippingSites();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParam = searchParams.get(SEARCH_PARAM) || '';

  const setSearchParam = useCallback(
    (values: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (values.length) {
          next.set(SEARCH_PARAM, values.join(','));
        } else {
          next.delete(SEARCH_PARAM);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // sync grid filter with url param
  useEffect(() => {
    const gridApi = grid.current?.api;
    if (!gridApi) return;

    const currentFilterValue = (
      gridApi.getColumnFilterModel<{ values: string[] }>(TARGET_COLUMN)
        ?.values || []
    ).join(',');

    if (currentFilterValue === searchParam) {
      // no change
      return;
    }

    const newValues = searchParam.split(',').filter(Boolean);
    gridApi.setColumnFilterModel(TARGET_COLUMN, {
      filterType: 'set',
      values: newValues,
    });

    // notify grid of filter change
    gridApi.onFilterChanged('columnFilter');
  }, [grid, searchParam]);

  // sync url param with grid filter
  useEffect(() => {
    const gridApi = grid.current?.api;
    if (!gridApi) {
      return;
    }

    const handleFilterChanged = (e: FilterChangedEvent) => {
      const isFromTargetColumn = e.columns.some(
        (col) => col.getColDef().field === TARGET_COLUMN,
      );
      if (!isFromTargetColumn) {
        return;
      }

      const filterModel = gridApi.getColumnFilterModel<{
        values: string[];
      }>(TARGET_COLUMN);
      const values = filterModel?.values || [];
      setSearchParam(values);
    };

    gridApi.addEventListener('filterChanged', handleFilterChanged);
    return () => {
      gridApi.removeEventListener('filterChanged', handleFilterChanged);
    };
  }, [grid, setSearchParam]);

  const selectedSites = useMemo((): SiteRef[] => {
    const selectedSiteIds = searchParam?.split(',').filter(Boolean) || [];
    return sites?.filter((site) => selectedSiteIds.includes(site.id)) ?? [];
  }, [searchParam, sites]);

  return (
    <MultiSelect
      value={selectedSites}
      onChange={(e) => {
        setSearchParam(e.value.map((s: SiteRef) => s.id));
      }}
      placeholder={t('shipping.selectShipFromSite')}
      options={sites}
      optionLabel="name"
      display="chip"
    />
  );
}
